// TiffinDashboard.jsx - Fixed version with proper mode switching
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Grid, Typography, Paper, Card, CardContent, TextField,
    CircularProgress, Button, IconButton, Tooltip, Chip, Divider,
    useMediaQuery, useTheme, Fade, Zoom, Grow, LinearProgress,
    Avatar, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TableSortLabel, MenuItem, Select, FormControl, InputLabel,
    Alert, Collapse, Switch, FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../../../lib/axios';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { selectGlobalProperty, setGlobalProperty } from '../../../../redux/propertySlice';
import { useSelector, useDispatch } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

// Initialize dayjs plugins
dayjs.extend(weekOfYear);
dayjs.extend(advancedFormat);

const TiffinDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isSwitchingMode, setIsSwitchingMode] = useState(false);
    const globalProperty = useSelector(selectGlobalProperty);
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [error, setError] = useState(null);
    const [isPropertyReady, setIsPropertyReady] = useState(false);

    const [isTodayMode, setIsTodayMode] = useState(true);
    const [animatedStats, setAnimatedStats] = useState({
        totalOrders: 0,
        deliveredQuantity: 0,
        returnedQuantity: 0,
        missingQuantity: 0,
        totalTiffins: 0 
    });

    const [stats, setStats] = useState({
        totalOrders: 0,
        totalDelivered: 0,
        totalReturned: 0,
        totalMissing: 0,
        deliveredQuantity: 0,
        returnedQuantity: 0,
        missingQuantity: 0,
        totalTiffins: 0,
        deliveryRate: 0,
        returnRate: 0,
        missingRate: 0
    });

    // Time-based data for charts
    const [dailyData, setDailyData] = useState([]);
    const [weeklyData, setWeeklyData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [mealTypeData, setMealTypeData] = useState([]);
    const [instituteData, setInstituteData] = useState([]);

    // Initialize property from localStorage on component mount
    useEffect(() => {
        const initializeProperty = () => {
            const savedPropertyId = localStorage.getItem('selectedPropertyId');
            const isLocked = localStorage.getItem('propertyLocked') === 'true';
            
            console.log('TiffinDashboard - Initializing property:', { 
                savedPropertyId, 
                isLocked, 
                currentGlobalProperty: globalProperty 
            });
            
            if (savedPropertyId && isLocked && !globalProperty) {
                dispatch(setGlobalProperty(parseInt(savedPropertyId)));
                console.log('TiffinDashboard - Restored property from localStorage:', savedPropertyId);
            }
            
            setIsPropertyReady(true);
        };
        
        initializeProperty();
    }, [dispatch, globalProperty]);

    // Animate stats on load
    useEffect(() => {
        const duration = 1000;
        const stepDuration = 20;
        const steps = duration / stepDuration;

        let step = 0;
        const interval = setInterval(() => {
            step++;
            const progress = step / steps;

            setAnimatedStats({
                totalOrders: Math.floor(stats.totalOrders * progress),
                deliveredQuantity: Math.floor(stats.deliveredQuantity * progress),
                returnedQuantity: Math.floor(stats.returnedQuantity * progress),
                missingQuantity: Math.floor(stats.missingQuantity * progress),
                 totalTiffins: Math.floor((stats.totalTiffins || 0) * progress)
            });

            if (step >= steps) {
                clearInterval(interval);
            }
        }, stepDuration);

        return () => clearInterval(interval);
    },[stats.totalOrders, stats.deliveredQuantity, stats.returnedQuantity, stats.missingQuantity, stats.totalTiffins]);

    const fetchOrders = async (todayModeValue = isTodayMode) => {
    setOrders([]);
    setFilteredOrders([]);
    setDailyData([]);
    setWeeklyData([]);
    setMonthlyData([]);
    setMealTypeData([]);
    setInstituteData([]);
    setStats({
        totalOrders: 0,
        totalDelivered: 0,
        totalReturned: 0,
        totalMissing: 0,
        deliveredQuantity: 0,
        returnedQuantity: 0,
        missingQuantity: 0,
        totalTiffins: 0,
        deliveryRate: 0,
        returnRate: 0,
        missingRate: 0
    });
    
    if (!isPropertyReady) return;
    
    if (!globalProperty) {
        console.warn('TiffinDashboard - No global property selected');
        toast.warning('Please select a property first');
        setLoading(false);
        return;
    }
    
    setLoading(true);
    setError(null);

    try {
        const UserId = sessionStorage?.getItem('UserId');
        
        if (!UserId) {
            toast.error('User not found. Please login again.');
            return;
        }

        // Determine flag based on mode parameter
        let flag = todayModeValue ? "TodayOrder" : "ReturnOrder";
        
        // For non-today mode, use empty flag for all orders
        if (!todayModeValue) {
            flag = ""; // Empty flag should fetch all orders
        }

        const payload = {
            Id: 1,
            Flag: flag,
            PropId: parseInt(globalProperty)
        };

        console.log('TiffinDashboard - Fetching orders with payload:', payload, 'Mode:', todayModeValue ? 'Today' : 'All');

        const response = await axiosInstance.post('TrnTiffin/RetriveTrnTiffin', payload);

        if (response.data && (response.data.Status === 0 || response.data.Status === 200)) {
            if (response.data.Data && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                setOrders(response.data.Data);
                setFilteredOrders(response.data.Data);
                calculateStats(response.data.Data);
                processTimeBasedData(response.data.Data);
                processMealTypeData(response.data.Data);
                processInstituteData(response.data.Data);
                toast.success(`${todayModeValue ? "Today's" : 'All'} orders loaded successfully!`);
            } else {
                setOrders([]);
                setFilteredOrders([]);
                calculateStats([]);
                processTimeBasedData([]);
                processMealTypeData([]);
                processInstituteData([]);
                toast.info(`No ${todayModeValue ? "today's" : ''} orders found for this property`);
            }
        }
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
        let errorMessage = 'Failed to load dashboard data. ';
        if (error.response?.data?.Message) {
            errorMessage += error.response.data.Message;
        } else if (error.message) {
            errorMessage += error.message;
        }
        setError(errorMessage);
        toast.error(errorMessage);
    } finally {
        setLoading(false);
        setIsSwitchingMode(false);
    }
};

const handleOrderModeToggle = (event) => {
    const newMode = event.target.checked;
    console.log('Switching mode from', isTodayMode, 'to', newMode);
    
    // Clear all existing data BEFORE changing mode
    setOrders([]);
    setFilteredOrders([]);
    setDailyData([]);
    setWeeklyData([]);
    setMonthlyData([]);
    setMealTypeData([]);
    setInstituteData([]);
    setStats({
        totalOrders: 0,
        totalDelivered: 0,
        totalReturned: 0,
        totalMissing: 0,
        deliveredQuantity: 0,
        returnedQuantity: 0,
        missingQuantity: 0,
        totalTiffins: 0,
        deliveryRate: 0,
        returnRate: 0,
        missingRate: 0
    });
    setAnimatedStats({
        totalOrders: 0,
        deliveredQuantity: 0,
        returnedQuantity: 0,
        missingQuantity: 0,
        totalTiffins: 0
    });
    
    setIsTodayMode(newMode);
    setIsSwitchingMode(true);
    
    if (newMode) {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs());
    }
    
    // Pass the new mode directly to ensure correct flag
    fetchOrders(newMode);
};

    // Calculate Statistics
    const calculateStats = (data) => {
        let totalOrders = data.length;
        let deliveredOrders = data.filter(o => o.Tfn_ReqSt?.toLowerCase() === 'delivered' || o.Tfn_ReqSt?.toLowerCase() === 'returned').length;
        let returnedOrders = data.filter(o => o.Tfn_ReqSt?.toLowerCase() === 'returned').length;

        let totalDeliveredQty = data.reduce((sum, o) => sum + (o.Dlv_Qty || 0), 0);
        let totalReturnedQty = data.reduce((sum, o) => sum + (o.Rtn_Qty || 0), 0);
        let totalMissingQty = totalDeliveredQty - totalReturnedQty;
        let totalTiffins = data.reduce((sum, o) => sum + (o.Req_Qty || 0), 0); 

        setStats({
            totalOrders,
            totalDelivered: deliveredOrders,
            totalReturned: returnedOrders,
            totalMissing: totalOrders - deliveredOrders,
            deliveredQuantity: totalDeliveredQty,
            returnedQuantity: totalReturnedQty,
            missingQuantity: totalMissingQty > 0 ? totalMissingQty : 0,
               totalTiffins,
            deliveryRate: totalOrders > 0 ? (deliveredOrders / totalOrders) * 100 : 0,
            returnRate: totalDeliveredQty > 0 ? (totalReturnedQty / totalDeliveredQty) * 100 : 0,
            missingRate: totalDeliveredQty > 0 ? (totalMissingQty / totalDeliveredQty) * 100 : 0
        });
    };

    // Process time-based data
    const processTimeBasedData = (data) => {
        // Daily Data
        const dailyMap = new Map();
        data.forEach(order => {
            if (order.Req_Date) {
                const date = dayjs(order.Req_Date).format('YYYY-MM-DD');
                if (!dailyMap.has(date)) {
                    dailyMap.set(date, { date, delivered: 0, returned: 0, missing: 0 });
                }
                const record = dailyMap.get(date);
                record.delivered += order.Dlv_Qty || 0;
                record.returned += order.Rtn_Qty || 0;
                record.missing += (order.Dlv_Qty || 0) - (order.Rtn_Qty || 0);
            }
        });
        setDailyData(Array.from(dailyMap.values()).slice(-14).reverse());

        // Weekly Data
        const weeklyMap = new Map();
        data.forEach(order => {
            if (order.Req_Date) {
                const weekNum = dayjs(order.Req_Date).week();
                const year = dayjs(order.Req_Date).year();
                const weekKey = `${year}-W${weekNum}`;
                if (!weeklyMap.has(weekKey)) {
                    weeklyMap.set(weekKey, { week: `W${weekNum}`, delivered: 0, returned: 0, missing: 0 });
                }
                const record = weeklyMap.get(weekKey);
                record.delivered += order.Dlv_Qty || 0;
                record.returned += order.Rtn_Qty || 0;
                record.missing += (order.Dlv_Qty || 0) - (order.Rtn_Qty || 0);
            }
        });
        setWeeklyData(Array.from(weeklyMap.values()).slice(-8));

        // Monthly Data
        const monthlyMap = new Map();
        data.forEach(order => {
            if (order.Req_Date) {
                const month = dayjs(order.Req_Date).format('MMM YYYY');
                if (!monthlyMap.has(month)) {
                    monthlyMap.set(month, { month, delivered: 0, returned: 0, missing: 0 });
                }
                const record = monthlyMap.get(month);
                record.delivered += order.Dlv_Qty || 0;
                record.returned += order.Rtn_Qty || 0;
                record.missing += (order.Dlv_Qty || 0) - (order.Rtn_Qty || 0);
            }
        });
        setMonthlyData(Array.from(monthlyMap.values()));
    };

    // Process meal type data
    const processMealTypeData = (data) => {
        const mealMap = new Map();
        data.forEach(order => {
            const meal = order.MealTypeName || 'Unknown';
            if (!mealMap.has(meal)) {
                mealMap.set(meal, { name: meal, value: 0 });
            }
            mealMap.get(meal).value += order.Req_Qty || 0;
        });
        setMealTypeData(Array.from(mealMap.values()));
    };

    // Process institute data
    const processInstituteData = (data) => {
        const instituteMap = new Map();
        data.forEach(order => {
            const institute = order.InstituteName || 'Unknown';
            if (!instituteMap.has(institute)) {
                instituteMap.set(institute, { name: institute, orders: 0, delivered: 0 });
            }
            const record = instituteMap.get(institute);
            record.orders++;
            if (order.Dlv_Qty) record.delivered += order.Dlv_Qty;
        });
        setInstituteData(Array.from(instituteMap.values()).slice(0, 6));
    };

    // Filter by date range (only meaningful in AllOrder mode)
    const applyDateFilter = () => {
        let filtered = [...orders];

        if (startDate && endDate && orders.length > 0) {
            filtered = filtered.filter(order => {
                if (!order.Req_Date) return false;
                const orderDate = dayjs(order.Req_Date);
                return orderDate.isAfter(startDate.subtract(1, 'day')) && orderDate.isBefore(endDate.add(1, 'day'));
            });
        }

        setFilteredOrders(filtered);
        calculateStats(filtered);
        processTimeBasedData(filtered);
        processMealTypeData(filtered);
        processInstituteData(filtered);
    };

    // Reset filters
    const resetFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs());
        setFilteredOrders(orders);
        if (orders.length > 0) {
            calculateStats(orders);
            processTimeBasedData(orders);
            processMealTypeData(orders);
            processInstituteData(orders);
        }
    };

    // Get chart data - always daily view (removed viewType)
    const getChartData = () => {
        return dailyData;
    };

    // Format number with abbreviation
    const formatNumber = (num) => {
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    // Animated Gradient Card Component
    const AnimatedGradientCard = ({ title, value, icon, gradientColors, delay = 0 }) => {
        return (
            <Grow in={true} timeout={500 + delay}>
                <Card sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
                    color: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-10px) scale(1.02)',
                        boxShadow: `0 20px 40px rgba(0,0,0,0.2)`,
                    },
                }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="caption" sx={{
                                    fontSize: { xs: '10px', sm: '12px' },
                                    opacity: 0.9,
                                    letterSpacing: '0.5px'
                                }}>
                                    {title}
                                </Typography>
                                <Typography variant="h4" sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '22px', sm: '28px', md: '32px' },
                                    transition: 'all 0.3s ease',
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    {formatNumber(value)}
                                </Typography>
                            </Box>
                            <Avatar sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                width: { xs: 45, sm: 55 },
                                height: { xs: 45, sm: 55 },
                                transition: 'transform 0.3s ease',
                                '&:hover': { transform: 'rotate(360deg) scale(1.1)' },
                                backdropFilter: 'blur(5px)'
                            }}>
                                {icon}
                            </Avatar>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={value > 0 ? 100 : 0}
                            sx={{
                                height: 3,
                                mt: 2,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: 2,
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: 'white',
                                    transition: 'transform 1s ease'
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </Grow>
        );
    };

    // Progress Circle Component
    const ProgressCircle = ({ value, label, color, size = 90 }) => {
        const [progress, setProgress] = useState(0);

        useEffect(() => {
            const timer = setTimeout(() => setProgress(value), 100);
            return () => clearTimeout(timer);
        }, [value]);

        const radius = (size - 10) / 2;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (progress / 100) * circumference;

        return (
            <Zoom in={true} timeout={800}>
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <svg width={size} height={size}>
                            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e0e0e0" strokeWidth="6" />
                            <circle
                                cx={size / 2} cy={size / 2} r={radius}
                                fill="none" stroke={color} strokeWidth="6"
                                strokeDasharray={circumference}
                                strokeDashoffset={strokeDashoffset}
                                strokeLinecap="round"
                                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                                style={{ transition: 'stroke-dashoffset 1s ease' }}
                            />
                        </svg>
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: color }}>
                                {progress.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                {label}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Zoom>
        );
    };

    // Order Table Component
    const OrderTable = () => {
        const sortedOrders = [...filteredOrders].sort((a, b) => {
            if (sortBy === 'date') {
                return sortOrder === 'desc'
                    ? new Date(b.Req_Date) - new Date(a.Req_Date)
                    : new Date(a.Req_Date) - new Date(b.Req_Date);
            }
            if (sortBy === 'quantity') {
                return sortOrder === 'desc'
                    ? (b.Req_Qty || 0) - (a.Req_Qty || 0)
                    : (a.Req_Qty || 0) - (b.Req_Qty || 0);
            }
            return 0;
        });

        const getStatusChip = (status) => {
            const statusColors = {
                'Delivered': { bg: '#22c55e15', color: '#22c55e' },
                'Returned': { bg: '#f59e0b15', color: '#f59e0b' },
                'Booked': { bg: '#3b82f615', color: '#3b82f6' },
                'Pending': { bg: '#ef444415', color: '#ef4444' },
                'Cancelled': { bg: '#6b728015', color: '#6b7280' }
            };
            const colors = statusColors[status] || { bg: '#9ca3af15', color: '#9ca3af' };
            return (
                <Chip size="small" label={status || 'N/A'} sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600, fontSize: '11px' }} />
            );
        };

        if (filteredOrders.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No orders found</Typography>
                </Box>
            );
        }

        return (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Order ID</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>
                                <TableSortLabel
                                    active={sortBy === 'date'}
                                    direction={sortOrder}
                                    onClick={() => {
                                        setSortBy('date');
                                        setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                                    }}
                                >
                                    Date
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Institute</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Meal Type</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Qty</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Delivered</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Returned</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Balance</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedOrders.slice(0, 30).map((order, index) => {
                            const delivered = order.Dlv_Qty || 0;
                            const returned = order.Rtn_Qty || 0;
                            const missing = delivered - returned;
                            return (
                                <TableRow key={order.Tfn_ReqId || index} hover sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>#{order.Tfn_ReqId}</TableCell>
                                    <TableCell sx={{ fontSize: '12px' }}>{order.Req_Date ? dayjs(order.Req_Date).format('DD/MM/YYYY') : 'N/A'}</TableCell>
                                    <TableCell sx={{ fontSize: '12px' }}>{order.InstituteName?.slice(0, 20) || 'N/A'}</TableCell>
                                    <TableCell sx={{ fontSize: '12px' }}>{order.MealTypeName || 'N/A'}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '12px' }}>{order.Req_Qty || 0}</TableCell>
                                    <TableCell align="center" sx={{ color: '#22c55e', fontWeight: 500, fontSize: '12px' }}>{delivered}</TableCell>
                                    <TableCell align="center" sx={{ color: '#f59e0b', fontWeight: 500, fontSize: '12px' }}>{returned}</TableCell>
                                    <TableCell align="center" sx={{ color: missing > 0 ? '#ef4444' : '#22c55e', fontWeight: 500, fontSize: '12px' }}>
                                        {missing > 0 ? missing : '-'}
                                    </TableCell>
                                    <TableCell>{getStatusChip(order.Tfn_ReqSt)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // COLORS for charts
    const COLORS = ['#635bff', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

    
   useEffect(() => {
    if (isPropertyReady && globalProperty) {
        // Clear all data first
        setOrders([]);
        setFilteredOrders([]);
        setDailyData([]);
        setWeeklyData([]);
        setMonthlyData([]);
        setMealTypeData([]);
        setInstituteData([]);
        setStats({
            totalOrders: 0,
            totalDelivered: 0,
            totalReturned: 0,
            totalMissing: 0,
            deliveredQuantity: 0,
            returnedQuantity: 0,
            missingQuantity: 0,
            totalTiffins: 0,
            deliveryRate: 0,
            returnRate: 0,
            missingRate: 0
        });
        
        // Fetch orders for today mode by default
        fetchOrders(true); // Pass true explicitly for today mode
    }
}, [isPropertyReady, globalProperty]); 

    
    useEffect(() => {
        if (orders.length > 0 && !isTodayMode) {
            applyDateFilter();
        }
    }, [startDate, endDate, orders, isTodayMode]);

    const chartData = getChartData();

    
    if (!isPropertyReady) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <CircularProgress sx={{ color: '#635bff' }} />
                <Typography sx={{ ml: 2 }}>Initializing...</Typography>
            </Box>
        );
    }

    // Show warning if no property selected
    if (!globalProperty) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column',
                gap: 2
            }}>
                <Typography variant="h6" color="error">
                    No Property Selected
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Please select a property to view dashboard.
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={() => navigate('/dashboard')}
                    sx={{ bgcolor: '#635bff' }}
                >
                    Go to Dashboard
                </Button>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#f0f2f5',
                p: { xs: 1, sm: 2, md: 2.5 }
            }}>
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Animated Header with Filters */}
                <Fade in={true} timeout={600}>
                    <Paper sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        background: isTodayMode
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        color: 'white',
                        transition: 'background 0.5s ease'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton
                                    onClick={() => navigate('/masters/tiffin')}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                        Tiffin Dashboard
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {isTodayMode
                                            ? `Today's orders — ${dayjs().format('DD MMM YYYY')}`
                                            : 'All orders — Real-time analytics & insights'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {/* Date Pickers - Only visible in All Order mode */}
                                {!isTodayMode && (
                                    <>
                                        <Paper sx={{ p: 0.5, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                                            <DatePicker
                                                label="Start Date"
                                                value={startDate}
                                                onChange={(newValue) => setStartDate(newValue)}
                                                sx={{ width: '130px' }}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: {
                                                            '& .MuiInputBase-root': { color: 'white', fontSize: '12px' },
                                                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)', fontSize: '12px' },
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                                                            '& .MuiInputBase-input': { py: 1, px: 1 }
                                                        }
                                                    }
                                                }}
                                            />
                                        </Paper>
                                        <Paper sx={{ p: 0.5, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                                            <DatePicker
                                                label="End Date"
                                                value={endDate}
                                                onChange={(newValue) => setEndDate(newValue)}
                                                sx={{ width: '130px' }}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: {
                                                            '& .MuiInputBase-root': { color: 'white', fontSize: '12px' },
                                                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)', fontSize: '12px' },
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                                                            '& .MuiInputBase-input': { py: 1, px: 1 }
                                                        }
                                                    }
                                }}
                            />
                        </Paper>
                    </>
                )}

                <Tooltip title="Refresh Data">
                    <IconButton
                        onClick={() => fetchOrders(isTodayMode)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>

                <Button
                    variant="contained"
                    size="small"
                    onClick={resetFilters}
                    sx={{ bgcolor: 'white', color: isTodayMode ? '#667eea' : '#11998e', borderRadius: 2, '&:hover': { bgcolor: '#f0f0f0' } }}
                >
                    Reset Filters
                </Button>

                {/* Today / All Orders Toggle */}
                <Paper sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(8px)',
                    gap: 0.5
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarMonthIcon sx={{ fontSize: 16, color: !isTodayMode ? 'white' : 'rgba(255,255,255,0.5)' }} />
                        <Typography variant="body2" sx={{
                            fontWeight: !isTodayMode ? 700 : 400,
                            color: !isTodayMode ? 'white' : 'rgba(255,255,255,0.5)',
                            fontSize: { xs: '11px', sm: '13px' },
                            transition: 'all 0.3s'
                        }}>
                            All Orders
                        </Typography>
                    </Box>

                    <Switch
                        checked={isTodayMode}
                        onChange={handleOrderModeToggle}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.4)' },
                            '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.25)' },
                        }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TodayIcon sx={{ fontSize: 16, color: isTodayMode ? 'white' : 'rgba(255,255,255,0.5)' }} />
                        <Typography variant="body2" sx={{
                            fontWeight: isTodayMode ? 700 : 400,
                            color: isTodayMode ? 'white' : 'rgba(255,255,255,0.5)',
                            fontSize: { xs: '11px', sm: '13px' },
                            transition: 'all 0.3s'
                        }}>
                            Today
                        </Typography>
                    </Box>
                </Paper>

                {/* Active Mode Badge */}
                <Chip
                    icon={isTodayMode ? <TodayIcon /> : <CalendarMonthIcon />}
                    label={isTodayMode ? "Today's Orders" : "All Orders"}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { xs: '11px', sm: '12px' },
                        '& .MuiChip-icon': { color: 'white' }
                    }}
                />
            </Box>
        </Box>
    </Paper>
</Fade>

                {/* Error Alert */}
                <Collapse in={!!error}>
                    <Alert severity="error" sx={{ mb: 0.5, borderRadius: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Collapse>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress sx={{ color: '#635bff' }} />
                        <Typography sx={{ ml: 2, color: '#635bff' }}>Loading dashboard data...</Typography>
                    </Box>
                ) : (
                    <>
                        {/* Animated Gradient Stats Cards */}
                        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                            <Grid item xs={6} sm={2.4}>
                                <AnimatedGradientCard
                                    title="Total Orders"
                                    value={animatedStats.totalOrders}
                                    icon={<RestaurantIcon />}
                                    gradientColors={['#667eea', '#764ba2']}
                                    delay={0}
                                />
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <AnimatedGradientCard
        title="Total Tiffins"
        value={animatedStats.totalTiffins}
        icon={<RestaurantIcon />}
        gradientColors={['#1CB5E0', '#000046']}
        delay={100}  
    />
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <AnimatedGradientCard
                                    title="Delivered Tiffins"
                                    value={animatedStats.deliveredQuantity}
                                    icon={<DeliveryDiningIcon />}
                                    gradientColors={['#11998e', '#38ef7d']}
                                    delay={100}
                                />
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <AnimatedGradientCard
                                    title="Returned Tiffins"
                                    value={animatedStats.returnedQuantity}
                                    icon={<InventoryIcon />}
                                    gradientColors={['#f2994a', '#f2c94c']}
                                    delay={200}
                                />
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <AnimatedGradientCard
                                    title="Balance Tiffins"
                                    value={animatedStats.missingQuantity}
                                    icon={<WarningAmberIcon />}
                                    gradientColors={['#eb3349', '#f45c43']}
                                    delay={300}
                                />
                            </Grid>
                        </Grid>

                        {/* Progress Circles */}
                        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                            <Grid item xs={4}>
                                <Paper sx={{
                                    p: 2, borderRadius: 3, textAlign: 'center',
                                    transition: 'all 0.3s',
                                    '&:hover': { transform: 'scale(1.02)', boxShadow: '0 10px 30px rgba(99, 91, 255, 0.2)' }
                                }}>
                                    <ProgressCircle value={stats.deliveryRate} label="Delivery Rate" color="#635bff" size={isMobile ? 70 : 90} />
                                </Paper>
                            </Grid>
                            <Grid item xs={4}>
                                <Paper sx={{
                                    p: 2, borderRadius: 3, textAlign: 'center',
                                    transition: 'all 0.3s',
                                    '&:hover': { transform: 'scale(1.02)', boxShadow: '0 10px 30px rgba(245, 158, 11, 0.2)' }
                                }}>
                                    <ProgressCircle value={stats.returnRate} label="Return Rate" color="#f59e0b" size={isMobile ? 70 : 90} />
                                </Paper>
                            </Grid>
                            <Grid item xs={4}>
                                <Paper sx={{
                                    p: 2, borderRadius: 3, textAlign: 'center',
                                    transition: 'all 0.3s',
                                    '&:hover': { transform: 'scale(1.02)', boxShadow: '0 10px 30px rgba(239, 68, 68, 0.2)' }
                                }}>
                                    <ProgressCircle value={stats.missingRate} label="Balance Rate" color="#ef4444" size={isMobile ? 70 : 90} />
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Charts Section */}
                        {chartData.length > 0 && (
                            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 1, sm: 1 } }}>
                                <Grid item xs={12} md={7}>
                                    <Zoom in={true} timeout={800}>
                                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
                                                📊 Delivery Analytics (Daily View)
                                            </Typography>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <AreaChart data={chartData}>
                                                    <defs>
                                                        <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                                                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                                        </linearGradient>
                                                        <linearGradient id="colorReturned" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                                                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                                    <YAxis tick={{ fontSize: 10 }} />
                                                    <RechartsTooltip />
                                                    <Legend />
                                                    <Area type="monotone" dataKey="delivered" stroke="#22c55e" fill="url(#colorDelivered)" name="Delivered" />
                                                    <Area type="monotone" dataKey="returned" stroke="#f59e0b" fill="url(#colorReturned)" name="Returned" />
                                                    <Bar dataKey="missing" fill="#ef4444" name="Missing" radius={[4, 4, 0, 0]} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </Paper>
                                    </Zoom>
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <Zoom in={true} timeout={1000}>
                                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, height: '100%' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '14px', sm: '16px' } }}>
                                                🥗 Meal Type Distribution
                                            </Typography>
                                            {mealTypeData.length > 0 ? (
                                                <>
                                                    <ResponsiveContainer width="100%" height={240}>
                                                        <PieChart>
                                                            <Pie
                                                                data={mealTypeData}
                                                                cx="50%" cy="50%"
                                                                innerRadius={60} outerRadius={80}
                                                                paddingAngle={5}
                                                                dataKey="value"
                                                                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                                                labelLine={false}
                                                            >
                                                                {mealTypeData.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                ))}
                                                            </Pie>
                                                            <RechartsTooltip />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                                        {mealTypeData.map((item, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={`${item.name}: ${item.value}`}
                                                                size="small"
                                                                sx={{ bgcolor: `${COLORS[idx % COLORS.length]}15`, color: COLORS[idx % COLORS.length], fontSize: '18px' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </>
                                            ) : (
                                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                                    No meal type data available
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Zoom>
                                </Grid>
                            </Grid>
                        )}

                        {/* Institute Performance */}
                        {instituteData.length > 0 && (
                            <Zoom in={true} timeout={1200}>
                                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
                                        🏫 Top Institutes Performance
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {instituteData.map((institute, idx) => (
                                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                                <Box sx={{
                                                    p: 1.5, borderRadius: 2, bgcolor: '#f8f9fa',
                                                    transition: 'all 0.3s',
                                                    '&:hover': { transform: 'translateX(8px)', bgcolor: '#f0f0f0' }
                                                }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{institute.name.slice(0, 25)}</Typography>
                                                        <Chip label={`${institute.orders} orders`} size="small" sx={{ height: 20, fontSize: '10px' }} />
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={stats.deliveredQuantity > 0 ? (institute.delivered / stats.deliveredQuantity) * 100 : 0}
                                                        sx={{ height: 6, borderRadius: 3, bgcolor: '#e0e0e0' }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                        {institute.delivered} tiffins delivered
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            </Zoom>
                        )}

                        {/* Orders Table */}
                        <Fade in={true} timeout={1400}>
                            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{
                                    p: 2, borderBottom: '1px solid #e0e0e0',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    flexWrap: 'wrap', gap: 1
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '14px', sm: '16px' } }}>
                                            📋 {isTodayMode ? "Today's" : 'All'} Orders ({filteredOrders.length} records)
                                        </Typography>
                                        <Chip
                                            label={isTodayMode ? '📅 Today' : '📆 All Time'}
                                            size="small"
                                            sx={{
                                                bgcolor: isTodayMode ? '#667eea15' : '#11998e15',
                                                color: isTodayMode ? '#667eea' : '#11998e',
                                                fontWeight: 600,
                                                fontSize: '11px'
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip
                                            label={`✅ Delivered: ${stats.totalDelivered}`}
                                            size="small"
                                            sx={{ bgcolor: '#22c55e15', color: '#22c55e', fontWeight: 600 }}
                                        />
                                        <Chip
                                            label={`↩️ Returned: ${stats.totalReturned}`}
                                            size="small"
                                            sx={{ bgcolor: '#f59e0b15', color: '#f59e0b', fontWeight: 600 }}
                                        />
                                    </Box>
                                </Box>
                                <OrderTable />
                                {filteredOrders.length > 30 && (
                                    <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #e0e0e0' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Showing 30 of {filteredOrders.length} orders
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Fade>

                        {/* Summary Footer */}
                        <Fade in={true} timeout={1600}>
                            <Box sx={{
                                mt: 3, p: 2, bgcolor: 'white', borderRadius: 3,
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', flexWrap: 'wrap', gap: 2
                            }}>
                                <Typography variant="caption" color="text.secondary">
                                    🕐 Last updated: {dayjs().format('DD/MM/YYYY HH:mm:ss')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <RestaurantIcon sx={{ fontSize: 12 }} /> Total: {stats.totalOrders}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#22c55e' }}>
                                        <DeliveryDiningIcon sx={{ fontSize: 12 }} /> Delivered: {stats.deliveredQuantity}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#f59e0b' }}>
                                        <InventoryIcon sx={{ fontSize: 12 }} /> Returned: {stats.returnedQuantity}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#ef4444' }}>
                                        <WarningAmberIcon sx={{ fontSize: 12 }} /> Balance: {stats.missingQuantity}
                                    </Typography>
                                </Box>
                            </Box>
                        </Fade>
                    </>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default TiffinDashboard;